import * as Sentry from '@sentry/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  APIError,
  APIErrorType,
  type GetProductCategoriesQuery,
  type GetProductCategoriesQueryVariables,
  useGetProductCategoriesQuery,
  useUploadImageMutation,
} from '@vizit/api';
import { SelectedFileStatus } from '@vizit/atoms';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { useDebounce } from '@vizit/hooks';
import type { CategoryItem } from '@vizit/molecules';
import type { ScoreAnImageFormSchema } from '@vizit/organisms';
import { type S3UploadFields, uploadToS3 } from '@vizit/s3';
import isEmpty from 'lodash.isempty';
import * as React from 'react';

const PAGE_SIZE = 10;

interface UseAssetUploadProps {
  /**
   * The ID of the organization to use for the upload
   *
   * @optional
   * @type {string}
   */
  organizationId?: string;
  /**
   * The ID of the portfolio to use for the upload
   *
   * @optional
   * @type {string}
   */
  workspaceId?: string;
  /**
   * The function to call when the image is uploaded
   *
   * @param id - The ID of the image
   */
  onSuccessfulUpload: (id: string) => void;
  /**
   * The function to call when the image upload fails
   *
   * @param error - The error that occurred
   */
  onAuthenticationError: (error: unknown | APIError) => void;
}

interface UseProductCategoryListProps {
  /** Organization ID */
  organizationId?: string;
  /** Portfolio ID */
  workspaceId?: string;
  /** Function to handle authentication error */
  onAuthenticationError: (error: APIError) => void;
}

/**
 * Result interface for the product category list hook
 */
interface UseProductCategoryListResult {
  /** Array of category items with transformed breadcrumbs */
  items: CategoryItem[];
  /** Whether the initial data is loading */
  isLoading: boolean;
  /** Whether the next page is being fetched */
  isFetchingNextPage: boolean;
  /** Whether there are more pages to load */
  hasNextPage: boolean;
  /** Current filter text for searching categories */
  filterText: string;
  /** Function to update the filter text */
  setFilterText: (text: string) => void;
  /** Function to load more items (next page) */
  loadMore: () => void;
}

const useAssetUpload = (props: UseAssetUploadProps) => {
  const { organizationId, workspaceId, onSuccessfulUpload, onAuthenticationError } = props;

  const { mutateAsync } = useUploadImageMutation();

  const [uploadStatus, setUploadStatus] = React.useState<SelectedFileStatus | undefined>(SelectedFileStatus.Ready);

  const handleOnSubmit = async ({ file, category, assetType, productTitle }: ScoreAnImageFormSchema) => {
    Sentry.addBreadcrumb({
      message: 'Starting asset file upload.',
      level: 'info',
      data: { file_name: file.name, file_size: file.size, asset_type: assetType, category },
    });

    setUploadStatus(SelectedFileStatus.Uploading);

    try {
      //   Cannot use mutate because we need the form to wait for the S3 upload to finish.
      const responseData = await mutateAsync({
        //@ts-ignore: this is protected by the router and will always have a value.
        workspace_id: workspaceId,
        organization_id: organizationId,
        image_type: assetType as 'hero' | 'carousel',
        name: file.name,
        product_category_id: category,
        // Product title is required for user-uploaded hero images.
        product_title: assetType === 'hero' ? productTitle : undefined,
      });

      if (responseData?.upload_fields && responseData?.id && responseData?.url) {
        Sentry.addBreadcrumb({
          message: 'Asset data record created, starting S3 upload',
          level: 'info',
          data: { asset_id: responseData.id, organization_id: organizationId, workspace_id: workspaceId },
        });

        // Parse upload fields for S3 upload request.
        const uploadFields = JSON.parse(responseData?.upload_fields) as S3UploadFields;

        // If upload to S3 is successful.
        const onSuccess = async () => {
          Sentry.addBreadcrumb({
            message: 'Asset upload completed.',
            level: 'info',
            data: { asset_id: responseData.id, organization_id: organizationId, workspace_id: workspaceId },
          });

          setUploadStatus(SelectedFileStatus.Success);

          if (responseData?.id) {
            // Slight delay to ensure the upload status is set to success.
            const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
            await delay(200);

            onSuccessfulUpload(responseData?.id);
          }
        };

        // If upload to S3 fails.
        const onError = (error?: Error) => {
          setUploadStatus(SelectedFileStatus.Error);

          Sentry.captureException(error, {
            level: 'error',
            tags: {
              feature: SENTRY_FEATURE_TAGS.Asset,
              stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Asset].assetS3Upload,
            },
            extra: {
              asset_id: responseData.id,
              file_name: file.name,
              file_size: file.size,
              workspace_id: workspaceId,
              organization_id: organizationId,
              asset_type: assetType,
            },
          });
        };

        // Upload image to S3 request.
        await uploadToS3(responseData.id, responseData.url, file, uploadFields, onSuccess, onError);
      } else {
        // If no upload fields are found, set the upload status to error.
        setUploadStatus(SelectedFileStatus.Error);

        const error = new Error('Missing fields from upload image response.');

        Sentry.captureException(error, {
          level: 'error',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Asset,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Asset].assetDataRecordCreation,
          },
          extra: {
            has_s3_upload_fields: !!responseData?.upload_fields,
            has_asset_id: !!responseData?.id,
            has_s3_url: !!responseData?.url,
            workspace_id: workspaceId,
            file_name: file.name,
            file_size: file.size,
            asset_type: assetType,
            organization_id: organizationId,
          },
        });
      }
    } catch (error) {
      setUploadStatus(SelectedFileStatus.Error);

      Sentry.captureException(error, {
        level: 'error',
        tags: {
          feature: SENTRY_FEATURE_TAGS.Asset,
          stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Asset].assetUpload,
        },
        extra: {
          file_name: file.name,
          file_size: file.size,
          asset_type: assetType,
          workspace_id: workspaceId,
          organization_id: organizationId,
        },
      });

      onAuthenticationError(error);
    }
  };

  React.useEffect(() => {
    return () => {
      // Reset the file status when component unmounts.
      setUploadStatus(SelectedFileStatus.Ready);
    };
  }, []);

  return {
    uploadStatus,
    onSubmit: handleOnSubmit,
    onFileStatusChange: setUploadStatus,
  };
};

/**
 * Hook for managing product category list with infinite scrolling and filtering
 * Uses TanStack Query for proper caching and background updates
 *
 * @returns Object containing category data and control functions
 */
function useProductCategoryList(props: UseProductCategoryListProps): UseProductCategoryListResult {
  const { organizationId, workspaceId, onAuthenticationError } = props;

  const [localFilterText, setLocalFilterText] = React.useState('');
  const debouncedFilterText = useDebounce(localFilterText, 300);

  const queryVariables: Omit<GetProductCategoriesQueryVariables, 'offset'> = React.useMemo(() => {
    return {
      // @ts-ignore: this is protected by the router and will always have a value.
      organization_id: organizationId,
      // @ts-ignore: this is protected by the router and will always have a value.
      workspace_id: workspaceId,
      breadcrumbs_like: isEmpty(debouncedFilterText) ? undefined : debouncedFilterText,
      limit: PAGE_SIZE,
      name_like: isEmpty(debouncedFilterText) ? undefined : debouncedFilterText,
      sort_by: 'name',
      sort_direction: 'asc',
    };
  }, [organizationId, workspaceId, debouncedFilterText]);

  const { data, error, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['GetProductCategoriesPages', queryVariables],
    queryFn: async ({ pageParam = 0 }) => {
      const response: GetProductCategoriesQuery = await useGetProductCategoriesQuery.fetcher({
        ...queryVariables,
        offset: pageParam,
      });

      const transformedCategories: CategoryItem[] = (response.data || []).map((category) => ({
        ...category,
        breadcrumbs: category.breadcrumbs.split(','),
      }));

      const hasData = response.data && response.data.length > 0;

      return {
        data: transformedCategories,
        // Only increment cursor if we received data, otherwise return undefined to indicate no more data
        // This was the glitch we were seeing on the internal demo 06.05.2025.
        nextCursor: hasData ? pageParam + 10 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: 0,
    enabled: !!workspaceId,
  });

  // Flatten all pages into a single items array with a duplicate ID check
  const items = React.useMemo(() => {
    const allPageItems = data?.pages.flatMap((page) => page.data) || [];

    const uniqueItems = allPageItems.reduce((uniqueItems, item) => {
      if (uniqueItems.findIndex((existingItem) => existingItem.id === item.id) === -1) {
        uniqueItems.push(item);
      }
      return uniqueItems;
    }, [] as CategoryItem[]);

    return uniqueItems;
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  React.useEffect(() => {
    if (error instanceof APIError && error.type === APIErrorType.NotAuthenticated) {
      onAuthenticationError(error);
    }
  }, [error, onAuthenticationError]);

  return {
    items,
    isLoading: isLoading,
    isFetchingNextPage: isFetchingNextPage,
    hasNextPage: hasNextPage || false,
    filterText: localFilterText,
    setFilterText: setLocalFilterText,
    loadMore,
  };
}

export { useProductCategoryList, useAssetUpload };
export type { UseProductCategoryListResult, UseAssetUploadProps };
